import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ArrowDown } from "lucide-react";
import { waLink } from "../config";
import { scrollToHash } from "./Header";

const LINES = [
  { text: "PURE.", accent: false },
  { text: "UNREFINED.", accent: true },
  { text: "TRADITION.", accent: false },
];

const MaskedLine = ({ text, delay, accent, testid }) => (
  <div className="overflow-hidden py-1">
    <motion.span
      data-testid={testid}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`block font-serif font-semibold leading-[0.92] tracking-tighter text-[13vw] md:text-[7.5vw] ${
        accent ? "text-gold italic font-medium" : "text-cream"
      }`}
    >
      {text}
    </motion.span>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bottleY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen bg-ink grain overflow-hidden flex items-center"
    >
      <div className="absolute -right-40 top-1/4 h-[34rem] w-[34rem] rounded-full bg-gold/10 blur-[120px] pointer-events-none" />
      <div className="absolute -left-32 bottom-0 h-96 w-96 rounded-full bg-olive/20 blur-[100px] pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-20 w-full grid md:grid-cols-12 gap-10 items-center">
        <motion.div style={{ y: textY }} className="md:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xs md:text-sm font-light tracking-[0.4em] uppercase text-gold mb-8"
          >
            Cold-Pressed · Wood Kolhu · Kachi Ghani
          </motion.p>

          <h1 className="mb-8">
            {LINES.map((l, i) => (
              <MaskedLine
                key={l.text}
                text={l.text}
                accent={l.accent}
                delay={0.35 + i * 0.18}
                testid={`hero-title-line-${i + 1}`}
              />
            ))}
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="max-w-md text-base md:text-lg font-light text-cream/70 leading-relaxed mb-10"
          >
            Cold-pressed oils made the slow way — wooden ghani, small batches,
            never above 45°C. Here's our seed, how we press it, and exactly
            what's in the bottle.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3 }}
            className="flex flex-wrap items-center gap-4"
          >
            <motion.a
              data-testid="hero-whatsapp-btn"
              href={waLink("Hi PHUDG! I'd like to order cold-pressed oils.")}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-medium tracking-wide text-ink hover:bg-amberbrand hover:text-cream transition-colors duration-300"
            >
              <MessageCircle size={18} />
              Order on WhatsApp
            </motion.a>
            <motion.button
              data-testid="hero-explore-btn"
              onClick={() => scrollToHash("#oils")}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
              className="rounded-full border border-cream/25 px-8 py-4 text-sm font-light tracking-widest uppercase text-cream/80 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              Explore the Oils
            </motion.button>
          </motion.div>
        </motion.div>

        <div className="md:col-span-5 relative">
          <motion.div
            style={{ y: bottleY }}
            initial={{ opacity: 0, y: 80 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto w-4/5 md:w-full max-w-sm"
          >
            <div className="animate-float-slow">
              <div
                data-testid="hero-product-image"
                className="overflow-hidden rounded-t-[999px] rounded-b-3xl border border-gold/30 shadow-[0_40px_80px_-20px_rgba(212,175,55,0.25)]"
              >
                <img
                  src="/images/black-mustard.jpg"
                  alt="PHUDG cold-pressed black mustard oil bottle in a mustard field"
                  className="w-full aspect-[2/3] object-cover"
                />
              </div>
            </div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 1.5 }}
              className="absolute -right-4 md:-right-10 bottom-10 rounded-2xl border border-white/10 bg-ink/80 backdrop-blur-md px-5 py-4"
            >
              <p className="font-serif text-2xl text-gold">Small batches</p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-cream/60">
                Pressed for freshness
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        onClick={() => scrollToHash("#oils")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-cream/40 hover:text-gold transition-colors duration-300"
        aria-label="Scroll down"
      >
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="block"
        >
          <ArrowDown size={22} />
        </motion.span>
      </motion.button>
    </section>
  );
};
